/** CRM-3 Payments — Nexi pay-by-link TEST_MODE */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, CreditCard, Plus, Send, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATO_COLOR = {
  creato: "bg-zinc-100 text-zinc-700",
  link_inviato: "bg-sky-100 text-sky-700",
  in_attesa: "bg-amber-100 text-amber-700",
  pagato: "bg-emerald-100 text-emerald-700",
  fallito: "bg-rose-100 text-rose-700",
  scaduto: "bg-zinc-200 text-zinc-500",
};

export default function CRMPaymentsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [testMode, setTestMode] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/crm/payments`, { withCredentials: true });
      setItems(r.data.items || []); setTotal(r.data.total || 0);
      setTestMode(r.data.test_mode);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendLink = async (id) => {
    await axios.post(`${API}/api/admin/crm/payments/${id}/send-link`, { canale: "whatsapp" }, { withCredentials: true });
    load();
  };
  const markPaid = async (id) => {
    if (!confirm("Simulare pagamento ricevuto?")) return;
    await axios.post(`${API}/api/admin/crm/payments/${id}/test-mark-paid`, {}, { withCredentials: true });
    load();
  };

  return (
    <div className="p-4 md:p-6" data-testid="crm-payments-page">
      <div className="flex flex-col md:flex-row justify-between gap-2 mb-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2"><CreditCard className="w-5 h-5" /> Pagamenti ({total})</h2>
          {testMode && <p className="text-xs text-amber-600">⚠ TEST_MODE attivo · link mockati</p>}
        </div>
        <div className="flex gap-2">
        <a href={`${API}/api/admin/crm/export/payments?format=xlsx`} target="_blank" rel="noreferrer"
           data-testid="crm-payments-export-btn"
           className="px-3 py-2 text-xs rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200">Export</a>
        <button onClick={() => setShowCreate(true)} data-testid="crm-payment-new-btn"
          className="px-3 py-2 text-sm bg-zinc-900 text-white rounded flex items-center gap-1">
          <Plus className="w-4 h-4" /> Nuovo pay-by-link
        </button>
        </div>
      </div>
      {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> :
       items.length === 0 ? <p className="text-zinc-400 italic text-center py-6">Nessun pagamento</p> :
        <div className="space-y-2">
          {items.map((p) => (
            <div key={p.id} className="border border-zinc-200 rounded p-3 flex items-center justify-between gap-3" data-testid={`crm-pay-${p.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">€ {Number(p.amount_eur).toFixed(2)} · {p.causale}</p>
                <p className="text-xs text-zinc-500 truncate">{p.pay_url}</p>
              </div>
              <span className={`px-1.5 py-0.5 rounded text-[10px] ${STATO_COLOR[p.stato] || "bg-zinc-100"}`}>{p.stato}</span>
              {p.stato === "creato" && (
                <button onClick={() => sendLink(p.id)} className="p-1.5 hover:bg-zinc-100 rounded" title="Invia via WhatsApp" data-testid={`crm-pay-send-${p.id}`}>
                  <Send className="w-4 h-4" />
                </button>
              )}
              {testMode && p.stato !== "pagato" && (
                <button onClick={() => markPaid(p.id)} className="p-1.5 hover:bg-emerald-50 rounded text-emerald-700" title="Simula pagamento" data-testid={`crm-pay-mark-${p.id}`}>
                  <CheckCircle2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>}
      {showCreate && <PaymentCreate onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function PaymentCreate({ onClose, onCreated }) {
  const [form, setForm] = useState({ amount_eur: "", causale: "", expires_in_days: 7 });
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [selectedCust, setSelectedCust] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (custQuery.length < 2) { setCustResults([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/api/admin/crm/customers/search?q=${encodeURIComponent(custQuery)}`, { withCredentials: true })
        .then((r) => setCustResults(r.data.items || []));
    }, 250);
    return () => clearTimeout(t);
  }, [custQuery]);

  const submit = async () => {
    setError(null);
    if (!selectedCust || !form.amount_eur || !form.causale) { setError("Cliente + importo + causale obbligatori"); return; }
    try {
      await axios.post(`${API}/api/admin/crm/payments/create-link`, {
        ...form, customer_id: selectedCust.id, amount_eur: parseFloat(form.amount_eur),
      }, { withCredentials: true });
      onCreated();
    } catch (e) { setError(e.response?.data?.detail?.error_code || e.message); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="crm-payment-create-modal">
        <h3 className="text-lg font-semibold mb-3">Nuovo pay-by-link</h3>
        <div className="space-y-2">
          <div className="relative">
            <input value={custQuery} onChange={(e) => { setCustQuery(e.target.value); setSelectedCust(null); }}
              placeholder="Cliente..." className="w-full px-3 py-2 text-sm rounded border border-zinc-200" />
            {custResults.length > 0 && !selectedCust && (
              <div className="absolute z-10 bg-white border border-zinc-200 rounded w-full max-h-40 overflow-y-auto">
                {custResults.map((c) => (
                  <button key={c.id} onClick={() => { setSelectedCust(c); setCustQuery(c.anagrafica?.email || ""); setCustResults([]); }}
                    className="block w-full text-left px-2 py-1 text-xs hover:bg-zinc-50">
                    {c.anagrafica?.ragione_sociale || `${c.anagrafica?.nome || ""} ${c.anagrafica?.cognome || ""}`}
                  </button>
                ))}
              </div>
            )}
          </div>
          <input type="number" step="0.01" placeholder="Importo €"
            onChange={(e) => setForm({ ...form, amount_eur: e.target.value })}
            className="w-full px-3 py-2 text-sm rounded border border-zinc-200" data-testid="crm-payment-amount" />
          <input placeholder="Causale (es. Rinnovo polizza auto)"
            onChange={(e) => setForm({ ...form, causale: e.target.value })}
            className="w-full px-3 py-2 text-sm rounded border border-zinc-200" />
          <input type="number" placeholder="Scade in giorni (default 7)" defaultValue={7}
            onChange={(e) => setForm({ ...form, expires_in_days: parseInt(e.target.value || 7) })}
            className="w-full px-3 py-2 text-sm rounded border border-zinc-200" />
        </div>
        {error && <p className="text-xs text-rose-600 mt-2">{String(error)}</p>}
        <div className="flex gap-2 mt-3">
          <button onClick={onClose} className="flex-1 py-2 text-sm rounded bg-zinc-100">Annulla</button>
          <button onClick={submit} className="flex-1 py-2 text-sm rounded bg-zinc-900 text-white" data-testid="crm-payment-submit-btn">Crea link</button>
        </div>
      </div>
    </div>
  );
}
